import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Paginated client-logos carousel. Shows PAGE_SIZE logos at a time. Auto-rotates
 * to the next page every N seconds (home_sections.clients_rotate_sec, default 8).
 * PAGE_SIZE also editable via home_sections.clients_page_size (default 18 = 3 rows × 6).
 */
export default function ClientsCarousel({ CLIENTS = [], home_sections = {}, tSections = {}, tCta = {} }) {
  const pageSize = Math.max(6, Number(home_sections.clients_page_size || 18));
  const rotateSec = Math.max(3, Number(home_sections.clients_rotate_sec || 8));
  // Escala do logo dentro da célula (% da altura). Antes era um cap fixo de
  // 40/48px numa célula de ~140px de altura — por isso as marcas subidas
  // apareciam minúsculas. Configurável em Hero & Cases · Layout.
  const logoScale = Math.min(95, Math.max(30, Number(home_sections.clients_logo_scale || 72)));
  const pages = Math.max(1, Math.ceil(CLIENTS.length / pageSize));
  const [page, setPage] = useState(0);
  const timerRef = useRef();

  useEffect(() => {
    if (pages <= 1) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setPage((p) => (p + 1) % pages), rotateSec * 1000);
    return () => clearInterval(timerRef.current);
  }, [pages, rotateSec]);

  const current = CLIENTS.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <section className="theme-dark py-24" data-testid="home-clients">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <h2 className="font-display text-3xl md:text-5xl text-white">{tSections.clients}</h2>
          <Link to="/sobre#historico" className="link-underline text-sm text-neutral-400" data-testid="home-clients-link">{tCta.allClients} →</Link>
        </div>

        <div className="relative">
          {pages > 1 && (
            <>
              <button
                onClick={() => setPage((p) => (p - 1 + pages) % pages)}
                className="hidden md:flex absolute -left-2 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white/5 hover:bg-white/15 border border-white/10 text-white transition-colors"
                aria-label="Anterior"
                data-testid="clients-prev"
              ><ChevronLeft className="w-4 h-4" /></button>
              <button
                onClick={() => setPage((p) => (p + 1) % pages)}
                className="hidden md:flex absolute -right-2 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white/5 hover:bg-white/15 border border-white/10 text-white transition-colors"
                aria-label="Próximo"
                data-testid="clients-next"
              ><ChevronRight className="w-4 h-4" /></button>
            </>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={page}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.45 }}
              className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4"
              data-testid="clients-grid"
            >
              {current.map((c, i) => (
                <div key={`${c.name}-${page}-${i}`} className="aspect-[3/2] border border-white/10 flex items-center justify-center text-center p-3 hover:border-accent-yellow transition group" data-testid={`home-client-${i}`}>
                  {c.logo ? (
                    <img src={c.logo.startsWith("http") ? c.logo : `${process.env.REACT_APP_BACKEND_URL}${c.logo}`} alt={c.name} loading="lazy"
                      style={{ height: `${logoScale}%`, width: `${Math.min(92, logoScale + 22)}%` }}
                      className="object-contain filter brightness-0 invert opacity-70 group-hover:opacity-100 group-hover:filter-none transition" />
                  ) : (
                    // No logo image → render the client name with the same visual weight
                    // as the logo images (filter brightness-0 invert makes logos white).
                    <span className="font-display font-semibold text-base md:text-xl leading-tight text-white/70 group-hover:text-white transition tracking-tight" data-testid={`client-name-${i}`}>{c.name}</span>
                  )}
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          {pages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2" data-testid="clients-dots">
              {Array.from({ length: pages }).map((_, i) => (
                <button key={i} onClick={() => setPage(i)} className={`h-1.5 rounded-full transition-all ${i === page ? "w-6 bg-accent-yellow" : "w-1.5 bg-white/25 hover:bg-white/50"}`} aria-label={`Página ${i + 1}`} data-testid={`clients-dot-${i}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
